/**
 * MarketplaceResults — marketplace search hits under a list page's own results (entity-management §5).
 * Demo page: /pricing-tables (plan cards) inside a NextAdmin widget `Card` (`top-products`).
 */
import { useQuery } from '@tanstack/react-query';
import { Globe, Lock } from 'lucide-react';
import { listMarketplaceListingsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { MarketplaceListing } from '../../generated/api/types.gen.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';

export interface MarketplaceResultsProps {
  /** Matches the server's marketplace entityType enum: 'agent_template' | 'tool' | 'workflow' | 'daemon' | ... */
  entityType: string;
  /** User's current search query from the list page. When empty, the section is hidden. */
  search: string;
  /** Called when the user clicks a listing. Parent decides whether to open a detail sheet,
   *  navigate, or trigger install directly. */
  onSelect?: (listing: MarketplaceListing) => void;
  /** Label shown in the section heading. Defaults to the entityType pluralized-ish. */
  label?: string;
}

/**
 * Reusable marketplace-search results block. Renders below the local results on
 * the Templates / Tools / Workforce list pages per entity-management guidelines
 * §5. Hits the listings route with `{ type, search }` in parallel with whatever
 * local query the parent is running.
 *
 * Silent when `search` is empty (marketplace browsing has its own dedicated
 * page). Loading + empty states are inline so the parent layout doesn't reflow.
 */
export function MarketplaceResults({ entityType, search, onSelect, label }: MarketplaceResultsProps) {
  const enabled = search.trim().length > 0;

  const { data, isLoading, isError } = useQuery({
    ...listMarketplaceListingsOptions({ query: { type: entityType, search, limit: 20 } }),
    enabled,
    staleTime: 30_000,
  });

  if (!enabled) return null;

  const listings = data?.data ?? [];
  const title = `Marketplace ${label ?? ''}`.trim();

  // When loading + zero results, still render the heading so the parent can
  // see that marketplace was queried — prevents "is search broken?" confusion.
  return (
    <Card title={title} actions={isLoading ? <span className="text-xs text-faint">Searching…</span> : undefined}>
      <div className="space-y-3">
        {!isLoading && !isError && <p className="text-xs text-faint">{listings.length} results</p>}

        {isError && <p className="text-xs text-danger">Could not search the marketplace.</p>}

        {listings.length === 0 && !isLoading && !isError && (
          <p className="text-xs text-faint">No marketplace listings match &ldquo;{search}&rdquo;.</p>
        )}

        {listings.length > 0 && !isError && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {listings.map((listing) => {
              const isPublic = listing.contentAccess === 'public';
              return (
                <Button
                  key={listing.id}
                  variant="secondary"
                  onClick={() => onSelect?.(listing)}
                  className="h-auto flex-col items-stretch gap-2 p-3 text-left font-normal"
                >
                  <span className="flex items-start justify-between gap-2">
                    <span className="line-clamp-1 text-sm font-medium text-heading">{listing.title}</span>
                    <Badge variant="purple">Marketplace</Badge>
                  </span>
                  <span className="line-clamp-2 text-xs text-muted">{listing.description || 'No description'}</span>
                  <span className="flex items-center gap-3 text-xs text-faint">
                    <span className="flex items-center gap-1">
                      {isPublic ? <Globe size={10} aria-hidden="true" /> : <Lock size={10} aria-hidden="true" />}
                      {isPublic ? 'Public' : 'Private'}
                    </span>
                    {listing.pricingModel === 'free' || !listing.priceUsd ? (
                      <span className="ml-auto font-medium text-success">Free</span>
                    ) : (
                      <span className="ml-auto font-medium text-accent-text">${listing.priceUsd}</span>
                    )}
                  </span>
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
